import { ApiClient } from './apiClient.js';
const DEFAULT_TOKEN_KEY = 'customer_token';
const GUEST_SESSION_STORAGE_KEY = 'agc.guestSession.v1';
/**
 * Phase 2 (410 Gone) sentinel: thrown when the backend has retired a guest
 * legacy auth endpoint that a stale PWA / native shell still calls (e.g.
 * `POST /auth/guest`). AuthContext catches this and clears local credentials
 * so the app falls back to the new GuestSession flow on next mount.
 *
 * Distinct from a generic Error so the handler logic is type-safe and
 * cannot accidentally fire on an unrelated 410 from a different endpoint.
 */
export class GuestAuthDeprecatedError extends Error {
    constructor(endpoint, message) {
        super(message ?? `Guest auth endpoint retired: ${endpoint}. Please refresh the app.`);
        this.name = 'GuestAuthDeprecatedError';
        this.endpoint = endpoint;
    }
}
const globalScope = typeof globalThis !== 'undefined' ? globalThis : undefined;
const isBrowser = typeof globalScope !== 'undefined' && typeof globalScope.localStorage !== 'undefined';
const buildQueryString = (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === null) {
            return;
        }
        if (Array.isArray(value)) {
            value.forEach((item) => params.append(key, item.toString()));
            return;
        }
        params.append(key, value.toString());
    });
    return params.toString();
};
const normalizeMediaItems = (rawMedia, fallbackBouquetId, fallbackCreatedAt, fallbackUpdatedAt) => {
    if (!Array.isArray(rawMedia)) {
        return [];
    }
    return rawMedia
        .map((item, index) => {
        if (!item || typeof item !== 'object') {
            return null;
        }
        const url = typeof item.url === 'string' ? item.url : null;
        if (!url) {
            return null;
        }
        const createdAt = typeof item.createdAt === 'string'
            ? item.createdAt
            : item.createdAt instanceof Date
                ? item.createdAt.toISOString()
                : fallbackCreatedAt;
        const updatedAt = typeof item.updatedAt === 'string'
            ? item.updatedAt
            : item.updatedAt instanceof Date
                ? item.updatedAt.toISOString()
                : fallbackUpdatedAt;
        return {
            id: item.id ? String(item.id) : `${fallbackBouquetId}-media-${index}`,
            bouquetId: item.bouquetId ? String(item.bouquetId) : fallbackBouquetId,
            type: item.type === 'VIDEO' ? 'VIDEO' : 'IMAGE',
            url,
            thumbnailUrl: typeof item.thumbnailUrl === 'string' ? item.thumbnailUrl : undefined,
            r2Key: item.r2Key ? String(item.r2Key) : `legacy/${fallbackBouquetId}/${index}`,
            position: typeof item.position === 'number' ? item.position : index,
            altText: typeof item.altText === 'string' ? item.altText : undefined,
            fileSize: typeof item.fileSize === 'number' ? item.fileSize : undefined,
            mimeType: typeof item.mimeType === 'string' ? item.mimeType : undefined,
            width: typeof item.width === 'number' ? item.width : undefined,
            height: typeof item.height === 'number' ? item.height : undefined,
            duration: typeof item.duration === 'number' ? item.duration : undefined,
            isProtected: typeof item.isProtected === 'boolean' ? item.isProtected : true,
            createdAt,
            updatedAt
        };
    })
        .filter((item) => item !== null)
        .sort((a, b) => a.position - b.position);
};
export class StorefrontApiClient extends ApiClient {
    constructor(baseURL, storageKey = DEFAULT_TOKEN_KEY) {
        super(baseURL);
        this.customerToken = null;
        this.storageKey = storageKey;
        if (isBrowser) {
            const savedToken = globalScope.localStorage.getItem(this.storageKey);
            if (savedToken) {
                this.setToken(savedToken);
            }
        }
    }
    setToken(token) {
        super.setToken(token);
        if (!token) {
            this.customerToken = null;
            return;
        }
        if (!token.startsWith('gs_')) {
            this.customerToken = token;
        }
    }
    getStoredGuestSession() {
        if (!isBrowser)
            return null;
        try {
            const raw = globalScope.localStorage.getItem(GUEST_SESSION_STORAGE_KEY);
            if (!raw)
                return null;
            const parsed = JSON.parse(raw);
            if (typeof parsed.token !== 'string' || !parsed.token.startsWith('gs_'))
                return null;
            if (typeof parsed.expiresAt !== 'string')
                return null;
            const expiresAt = new Date(parsed.expiresAt).getTime();
            if (!Number.isFinite(expiresAt) || expiresAt <= Date.now())
                return null;
            return {
                token: parsed.token,
                expiresAt: parsed.expiresAt,
            };
        }
        catch {
            return null;
        }
    }
    persistGuestSession(result) {
        if (!isBrowser)
            return;
        const meta = {
            token: result.token,
            expiresAt: result.session.expiresAt,
        };
        try {
            globalScope.localStorage.setItem(GUEST_SESSION_STORAGE_KEY, JSON.stringify(meta));
        }
        catch { /* localStorage may be unavailable */ }
    }
    isGuestCompatibleEndpoint(endpoint, method) {
        if (endpoint === '/guest-sessions/me' || endpoint === '/guest-sessions/me/orders') {
            return true;
        }
        // GET /orders list. Backend `GET /api/orders` (orders.ts) routes guest
        // sessions through `filters.guestSessionId = req.guestSession.id` after
        // Phase 0 — but that only works if the gs_-token reaches the request,
        // which means it must be attached for the *list* endpoint too, not only
        // for POST/individual-id endpoints. Without this branch, the storefront
        // ProfileOrdersScreen renders empty for any guest.
        //
        // Match `/orders` exactly OR `/orders?...` (query string).
        if ((endpoint === '/orders' || endpoint.startsWith('/orders?')) && method === 'GET') {
            return true;
        }
        if (endpoint === '/orders' && method === 'POST') {
            return true;
        }
        if (/^\/orders\/[^/]+$/.test(endpoint) && method === 'GET') {
            return true;
        }
        if (/^\/orders\/[^/]+\/(payment-status|receipt)$/.test(endpoint) && method === 'GET') {
            return true;
        }
        if (/^\/orders\/[^/]+\/cancel$/.test(endpoint) && method === 'POST') {
            return true;
        }
        // Guest must be able to retry a failed/cancelled BirPay payment from
        // PaymentCancelScreen without being forced to register. Backend gates
        // ownership via gs_-token + Order.guestSessionId.
        return /^\/orders\/[^/]+\/retry-payment$/.test(endpoint) && method === 'POST';
    }
    request(endpoint, options = {}) {
        const method = options.method || 'GET';
        const headers = { ...options.headers };
        if (!this.customerToken && !headers.Authorization && this.isGuestCompatibleEndpoint(endpoint, method)) {
            const guestSession = this.getStoredGuestSession();
            if (guestSession) {
                headers.Authorization = `Bearer ${guestSession.token}`;
            }
        }
        // Phase 2 (410 Gone) graceful handler: when the backend retires a legacy
        // guest auth endpoint (`/auth/guest`, `/auth/me` for gs_-tokens, etc.), a
        // cached PWA or older native shell may still call it. Translate the 410
        // into a typed sentinel so AuthContext can clear local creds quietly.
        return super.request(endpoint, { ...options, headers }).catch((err) => {
            const status = err?.response?.status;
            if (status === 410 && this.isLegacyGuestAuthEndpoint(endpoint)) {
                throw new GuestAuthDeprecatedError(endpoint);
            }
            throw err;
        });
    }
    /**
     * Endpoints whose 410 response specifically indicates the legacy guest auth
     * shim has been removed. Keep this list narrow — a 410 from any other
     * endpoint should bubble as a generic error so we don't accidentally drop
     * customer credentials on an unrelated removal.
     */
    isLegacyGuestAuthEndpoint(endpoint) {
        return endpoint === '/auth/guest' || endpoint.startsWith('/auth/guest?');
    }
    persistToken(token) {
        if (isBrowser) {
            if (token) {
                globalScope.localStorage.setItem(this.storageKey, token);
            }
            else {
                globalScope.localStorage.removeItem(this.storageKey);
            }
        }
        this.setToken(token);
    }
    normalizeBouquet(raw) {
        if (!raw) {
            throw new Error('Empty bouquet payload');
        }
        const bouquetId = raw.id ? String(raw.id) : 'unknown';
        const images = Array.isArray(raw.images)
            ? raw.images.filter((img) => typeof img === 'string' && img.length > 0)
            : typeof raw.image === 'string' && raw.image.length > 0
                ? [raw.image]
                : [];
        const categoryId = raw.category?.id ?? raw.categoryId ?? 'unknown';
        const categoryName = raw.category?.name ?? raw.categoryName ?? 'Unknown';
        const categoryDescription = raw.category?.description ?? raw.categoryDescription ?? undefined;
        const popularity = typeof raw.popularity === 'number'
            ? raw.popularity
            : raw.popularity != null
                ? Number(raw.popularity)
                : 0;
        const createdAt = typeof raw.createdAt === 'string'
            ? raw.createdAt
            : raw.createdAt instanceof Date
                ? raw.createdAt.toISOString()
                : new Date().toISOString();
        const updatedAt = typeof raw.updatedAt === 'string'
            ? raw.updatedAt
            : raw.updatedAt instanceof Date
                ? raw.updatedAt.toISOString()
                : createdAt;
        const price = typeof raw.price === 'number'
            ? raw.price
            : raw.price != null
                ? Number(raw.price)
                : 0;
        // 🎁 Normalize discount fields (Prisma Decimal to Number)
        const discount = raw.discount != null
            ? (typeof raw.discount === 'string' ? parseFloat(raw.discount) : Number(raw.discount))
            : undefined;
        const salePrice = raw.salePrice != null
            ? (typeof raw.salePrice === 'string' ? parseFloat(raw.salePrice) : Number(raw.salePrice))
            : undefined;
        const promotionText = typeof raw.promotionText === 'string' ? raw.promotionText : undefined;
        const imagePositionX = raw.imagePositionX != null
            ? Number(raw.imagePositionX)
            : undefined;
        const imagePositionY = raw.imagePositionY != null
            ? Number(raw.imagePositionY)
            : undefined;
        const mainImage = typeof raw.mainImage === 'string' && raw.mainImage.length > 0
            ? raw.mainImage
            : typeof raw.image === 'string' && raw.image.length > 0
                ? raw.image
                : images[0];
        const normalizedMedia = normalizeMediaItems(raw.media, bouquetId, createdAt, updatedAt);
        return {
            id: bouquetId,
            name: raw.name ?? '',
            nameEn: raw.nameEn ?? undefined,
            nameAz: raw.nameAz ?? undefined,
            price,
            discount, // 🎁 Discount fields
            salePrice, // 🎁
            promotionText, // 🎁
            mainImage,
            images,
            isActive: raw.isActive ?? true,
            isPopular: raw.isPopular ?? false,
            isNew: raw.isNew ?? false,
            inStock: raw.inStock ?? true,
            stockCount: typeof raw.stockCount === 'number' ? raw.stockCount : undefined,
            category: {
                id: String(categoryId),
                name: String(categoryName),
                nameEn: raw.category?.nameEn ?? undefined,
                nameAz: raw.category?.nameAz ?? undefined,
                description: categoryDescription
            },
            varieties: Array.isArray(raw.varieties) ? raw.varieties : undefined,
            colors: Array.isArray(raw.colors) ? raw.colors : undefined,
            occasion: typeof raw.occasion === 'string' ? raw.occasion : undefined,
            flowers: Array.isArray(raw.flowers) ? raw.flowers : undefined,
            popularity,
            addedDate: raw.addedDate ?? undefined,
            createdAt,
            updatedAt,
            media: normalizedMedia.length > 0 ? normalizedMedia : undefined,
            // 🆕 Bouquet type and availability
            type: raw.type ?? undefined,
            availableFromDate: raw.availableFromDate ?? undefined,
            deliverySlaMinutes: typeof raw.deliverySlaMinutes === 'number' ? raw.deliverySlaMinutes : undefined,
            // 🤖 AI-generated SEO content (Senti Module)
            seoTitleRu: typeof raw.seoTitleRu === 'string' ? raw.seoTitleRu : undefined,
            seoTitleEn: typeof raw.seoTitleEn === 'string' ? raw.seoTitleEn : undefined,
            seoTitleAz: typeof raw.seoTitleAz === 'string' ? raw.seoTitleAz : undefined,
            seoDescriptionRu: typeof raw.seoDescriptionRu === 'string' ? raw.seoDescriptionRu : undefined,
            seoDescriptionEn: typeof raw.seoDescriptionEn === 'string' ? raw.seoDescriptionEn : undefined,
            seoDescriptionAz: typeof raw.seoDescriptionAz === 'string' ? raw.seoDescriptionAz : undefined,
            seoKeywordsRu: Array.isArray(raw.seoKeywordsRu) ? raw.seoKeywordsRu : undefined,
            seoKeywordsEn: Array.isArray(raw.seoKeywordsEn) ? raw.seoKeywordsEn : undefined,
            seoKeywordsAz: Array.isArray(raw.seoKeywordsAz) ? raw.seoKeywordsAz : undefined,
            assistantFacts: Array.isArray(raw.assistantFacts) ? raw.assistantFacts : undefined,
            aiStatus: raw.aiStatus ?? undefined,
            slug: typeof raw.slug === 'string' ? raw.slug : undefined,
            imagePositionX,
            imagePositionY
        };
    }
    async login(credentials) {
        const result = await this.request('/auth/login', {
            method: 'POST',
            body: credentials
        });
        this.persistToken(result.token);
        return result;
    }
    async register(userData) {
        const result = await this.request('/auth/register', {
            method: 'POST',
            body: userData
        });
        this.persistToken(result.token);
        return result;
    }
    async logout() {
        this.persistToken(null);
    }
    /** Exchange a Google ID token for a session. */
    async loginWithGoogle(payload) {
        const result = await this.request('/auth/oauth/google', {
            method: 'POST',
            body: payload
        });
        this.persistToken(result.token);
        return result;
    }
    async createGuestSession() {
        const result = await this.request('/guest-sessions', {
            method: 'POST',
        });
        this.persistGuestSession(result);
        return result;
    }
    async getGuestSessionMe() {
        return this.request('/guest-sessions/me');
    }
    async getGuestSessionOrders() {
        return this.request('/guest-sessions/me/orders');
    }
    /** Request a 6-digit OTP code to the given email. */
    async requestOtp(payload) {
        return this.request('/auth/otp/request', {
            method: 'POST',
            body: payload,
        });
    }
    /** Exchange a 6-digit OTP code for a session. */
    async verifyOtp(payload) {
        const result = await this.request('/auth/otp/verify', {
            method: 'POST',
            body: payload,
        });
        this.persistToken(result.token);
        return result;
    }
    async getCurrentUser() {
        return this.request('/auth/me');
    }
    async updateProfile(data) {
        return this.request('/auth/me', {
            method: 'PATCH',
            body: data,
        });
    }
    async deleteAccount() {
        return this.request('/auth/me', {
            method: 'DELETE',
        });
    }
    async getBouquets(filters = {}, options = {}) {
        const query = buildQueryString(filters);
        const response = await this.request(`/bouquets${query ? `?${query}` : ''}`, {
            signal: options.signal
        });
        if (!response.availableVarieties && Array.isArray(response.bouquets)) {
            const varietySet = new Set();
            response.bouquets.forEach((bouquet) => {
                bouquet.varieties?.forEach((variety) => varietySet.add(variety));
            });
            response.availableVarieties = Array.from(varietySet);
        }
        const bouquets = Array.isArray(response.bouquets)
            ? response.bouquets.map((bouquet) => this.normalizeBouquet(bouquet))
            : [];
        const pricePool = bouquets
            .map((bouquet) => bouquet.price)
            .filter((price) => typeof price === 'number' && !Number.isNaN(price));
        const total = typeof response.total === 'number' ? response.total : bouquets.length;
        const page = typeof response.page === 'number' ? response.page : 1;
        const limitValue = typeof response.limit === 'number'
            ? response.limit
            : bouquets.length;
        const effectiveLimit = limitValue > 0 ? limitValue : 1;
        const totalPages = typeof response.totalPages === 'number'
            ? response.totalPages
            : Math.max(1, Math.ceil(total / effectiveLimit));
        const minPrice = typeof response.minPrice === 'number'
            ? response.minPrice
            : pricePool.length > 0
                ? Math.min(...pricePool)
                : null;
        const maxPrice = typeof response.maxPrice === 'number'
            ? response.maxPrice
            : pricePool.length > 0
                ? Math.max(...pricePool)
                : null;
        return {
            bouquets,
            availableVarieties: response.availableVarieties ?? [],
            total,
            page,
            limit: limitValue,
            totalPages,
            minPrice,
            maxPrice
        };
    }
    async getBouquetsForVarietyCalculation(filters = {}, options = {}) {
        const { flowerIds, selectedVarieties, ...rest } = filters;
        return this.getBouquets(rest, options);
    }
    async getBouquet(id, options = {}) {
        return this.getBouquetById(id, options);
    }
    async getCategories() {
        const response = await this.request('/bouquets/categories/all');
        if (Array.isArray(response)) {
            return response;
        }
        return response.data ?? [];
    }
    async getSimilarBouquets(bouquetId, options = {}) {
        const { limit = 12, language = 'ru' } = options;
        const params = new URLSearchParams();
        params.append('limit', limit.toString());
        params.append('language', language);
        const response = await this.request(`/bouquets/${bouquetId}/similar?${params.toString()}`);
        // Normalize bouquets in items
        const normalizedItems = response.items.map(item => ({
            bouquet: this.normalizeBouquet(item.bouquet),
            similarityScore: item.similarityScore
        }));
        return {
            bouquetId: response.bouquetId,
            total: response.total,
            items: normalizedItems
        };
    }
    async createOrder(orderData, idempotencyKey) {
        return this.request('/orders', {
            method: 'POST',
            body: orderData,
            // PR-3: optional idempotency. Caller (QuickCheckoutScreen) generates a
            // UUID per checkout session and persists it in checkout_draft so a page
            // reload mid-flow doesn't create a duplicate order on resubmit.
            ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
        });
    }
    async retryPayment(orderId) {
        return this.request(`/orders/${orderId}/retry-payment`, {
            method: 'POST'
        });
    }
    async getPaymentStatus(orderId) {
        return this.request(`/orders/${orderId}/payment-status`);
    }
    /**
     * Download receipt PDF for a paid order. Returns a Blob the caller can save
     * via URL.createObjectURL — preserves Authorization header (required for auth).
     */
    async downloadReceipt(orderId) {
        const { blob, filename } = await this.fetchBlob(`/orders/${orderId}/receipt`);
        return { blob, filename: filename ?? `qebz-${orderId}.pdf` };
    }
    async getMyOrders(filters = {}) {
        const query = buildQueryString(filters);
        return this.request(`/orders${query ? `?${query}` : ''}`);
    }
    async trackOrder(orderId) {
        return this.request(`/orders/${orderId}/tracking`);
    }
    async createScheduledOrder(orderData) {
        return this.request('/orders/scheduled-orders', {
            method: 'POST',
            body: orderData
        });
    }
    async getScheduledOrders(filters = {}) {
        const query = buildQueryString(filters);
        return this.request(`/orders/scheduled-orders${query ? `?${query}` : ''}`);
    }
    async updateScheduledOrder(orderId, updates) {
        return this.request(`/orders/scheduled-orders/${orderId}`, {
            method: 'PUT',
            body: updates
        });
    }
    async deleteScheduledOrder(orderId) {
        return this.request(`/orders/scheduled-orders/${orderId}`, {
            method: 'DELETE'
        });
    }
    async getChatMessages() {
        return this.request('/chat/messages');
    }
    async sendChatMessage(text) {
        return this.request('/chat/messages', {
            method: 'POST',
            body: { text }
        });
    }
    async markChatMessagesAsRead() {
        return this.request('/chat/messages/read', {
            method: 'PUT'
        });
    }
    async clearChatHistory() {
        return this.request('/chat/messages', {
            method: 'DELETE'
        });
    }
    async createOrderChat(orderId, initialMessage) {
        try {
            return await this.request('/chat/create-for-order', {
                method: 'POST',
                body: { orderId, initialMessage }
            });
        }
        catch (error) {
            const message = error instanceof Error
                ? error.message
                : typeof error === 'string'
                    ? error
                    : '';
            if (message.includes('Resource already exists') || message.includes('409')) {
                const chatsResponse = await this.getChats();
                const existingOrderChat = chatsResponse.data?.find((chat) => chat.orderId === orderId && Boolean(chat.id));
                if (existingOrderChat?.id) {
                    return {
                        success: true,
                        data: {
                            chatId: existingOrderChat.id,
                            orderId,
                            orderNumber: existingOrderChat.orderNumber || '',
                            messages: []
                        },
                        message: 'Chat already exists'
                    };
                }
                return {
                    success: false,
                    error: 'Chat already exists but could not be resolved'
                };
            }
            throw error;
        }
    }
    // ============================================================================
    // MULTI-CHAT API
    // ============================================================================
    /**
     * Получить список всех чатов пользователя
     */
    async getChats() {
        return this.request('/chat/chats');
    }
    /**
     * Получить сообщения конкретного чата
     */
    async getChatMessagesById(chatId, options) {
        const params = new URLSearchParams();
        if (options?.cursor)
            params.append('cursor', options.cursor);
        if (options?.limit)
            params.append('limit', options.limit.toString());
        const query = params.toString();
        return this.request(`/chat/chats/${chatId}/messages${query ? `?${query}` : ''}`);
    }
    /**
     * Отправить сообщение в конкретный чат
     */
    async sendMessageToChat(chatId, text) {
        return this.request(`/chat/chats/${chatId}/messages`, {
            method: 'POST',
            body: { text }
        });
    }
    /**
     * Отметить сообщения чата как прочитанные
     */
    async markChatAsRead(chatId) {
        return this.request(`/chat/chats/${chatId}/read`, {
            method: 'PUT'
        });
    }
    async getAvailableVarieties(filters = {}) {
        const query = buildQueryString(filters);
        const response = await this.request(`/bouquets/available-varieties${query ? `?${query}` : ''}`);
        return response.availableVarieties ?? [];
    }
    async getBouquetById(id, options = {}) {
        const result = await this.request(`/bouquets/${id}`, {
            signal: options.signal
        });
        if (result && typeof result === 'object' && 'success' in result) {
            const response = result;
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to load bouquet');
            }
            return this.normalizeBouquet(response.data);
        }
        return this.normalizeBouquet(result);
    }
    async getFlowers(filters = {}) {
        const query = buildQueryString(filters);
        return this.request(`/bouquets/flowers${query ? `?${query}` : ''}`);
    }
    async getFlowerTypes() {
        const response = await this.getFlowers();
        const flowers = response.flowers ?? [];
        const types = flowers
            .map((flower) => flower.type)
            .filter((type) => typeof type === 'string' && type.length > 0);
        return Array.from(new Set(types));
    }
    async getFlowerColors(categoryId) {
        // Backend now scopes the palette to colors actually present in active
        // in-stock bouquets (optionally inside `categoryId`). The legacy
        // `hasInStockBouquets` flag is always true in the new contract — kept
        // for shape compatibility with older clients.
        const trimmed = typeof categoryId === 'string' ? categoryId.trim() : '';
        const path = trimmed.length > 0
            ? `/flowers/colors?categoryId=${encodeURIComponent(trimmed)}`
            : '/flowers/colors';
        const response = await this.request(path);
        return (response.colors ?? []).map((c) => ({
            ...c,
            hasInStockBouquets: c.hasInStockBouquets ?? true,
        }));
    }
    async getAllFlowerVarieties(filters = {}, language = 'ru') {
        let availableVarietyIds = [];
        if (!filters.flowerIds) {
            const response = await this.getBouquets(filters);
            availableVarietyIds = response.availableVarieties;
        }
        else {
            availableVarietyIds = await this.getAvailableVarieties(filters);
        }
        if (availableVarietyIds.length === 0) {
            return [];
        }
        // Backend returns localized names (nameRu/nameEn/nameAz) after the
        // FlowerVariant hierarchy migration — there is no plain `name` field.
        // Resolve the display name per request language, falling back ru→en→az.
        const flowersResponse = await this.getFlowers({ ids: availableVarietyIds.join(',') });
        const flowers = flowersResponse.flowers ?? [];
        const pickName = (f) => {
            const byLang = language === 'en' ? f.nameEn :
                language === 'az' ? f.nameAz :
                    f.nameRu;
            return byLang || f.nameRu || f.nameEn || f.nameAz || f.name || '';
        };
        return flowers.map((flower) => ({
            id: flower.id,
            name: pickName(flower),
            type: flower.type,
            // Priority: the admin-uploaded filter image (filterImage, usually a
            // cf: ref) → legacy `image` field → first of `images[]` → empty string.
            // Empty string lets ImageWithFallback render an honest SVG placeholder
            // instead of silently substituting a stock Unsplash photo, which would
            // mislead the buyer about what the variant actually looks like.
            image: flower.filterImage || flower.image || flower.images?.[0] || '',
            filterOrder: flower.filterOrder ?? 99
        }));
    }
    async getRoseVarieties() {
        const allFlowers = await this.getAllFlowerVarieties();
        return allFlowers
            .filter((flower) => flower.type === 'розы' || flower.type === 'rose')
            .map((flower) => ({
            id: flower.id,
            name: flower.name,
            image: flower.image
        }));
    }
    async deleteHistoryOrder(orderId) {
        await this.request(`/orders/history/${orderId}`, {
            method: 'DELETE'
        });
    }
    // ============================================================================
    // 📊 Bestsellers Methods (Event-Driven Sales Stats)
    // ============================================================================
    /**
     * Get top bestselling bouquets based on real sales data (last 30 days)
     * @param limit - Number of bestsellers to fetch (default: 10, max: 50)
     * @param sortBy - Sort criteria: 'sales' (quantity), 'revenue', or 'orders' (default: 'sales')
     */
    async getBestsellers(limit = 10, sortBy = 'sales', options = {}) {
        const query = buildQueryString({ limit, sortBy });
        const response = await this.request(`/bouquets/bestsellers${query ? `?${query}` : ''}`, { signal: options.signal });
        // Normalize bouquets with sales metadata
        const bestsellers = response.bestsellers.map((item) => {
            const normalized = this.normalizeBouquet(item);
            return {
                ...normalized,
                salesMeta: item.salesMeta
            };
        });
        return {
            bestsellers,
            meta: response.meta
        };
    }
    // ============================================================================
    // 🎁 Referral Program Methods
    // ============================================================================
    /**
     * Generate a new referral code
     */
    async generateReferralCode(params) {
        return this.request('/referrals/generate', {
            method: 'POST',
            body: params
        });
    }
    /**
     * Validate a referral code before applying
     */
    async validateReferralCode(code) {
        return this.request(`/referrals/validate?code=${encodeURIComponent(code)}`);
    }
    /**
     * Apply a referral code to an order
     */
    async applyReferralCode(params) {
        return this.request('/referrals/apply', {
            method: 'POST',
            body: params
        });
    }
    /**
     * Get current user's referral balance and stats
     */
    async getReferralBalance() {
        return this.request('/referrals/balance');
    }
    /**
     * Get all referral codes for current user
     */
    async getUserReferralCodes() {
        return this.request('/referrals/codes');
    }
    /**
     * Get all referral rewards for current user
     */
    async getUserReferralRewards() {
        return this.request('/referrals/rewards');
    }
    /**
     * ✅ NEW: Track referral visit (optional analytics)
     */
    async trackReferralVisit(data) {
        return this.request('/referrals/track-visit', {
            method: 'POST',
            body: data
        });
    }
    // ============================================================================
    // 🏪 Public Stores
    // ============================================================================
    async getStores() {
        const response = await this.request('/stores');
        return response.stores ?? [];
    }
    // ============================================================================
    // 📞 Contact Settings
    // ============================================================================
    async getContactSettings() {
        const response = await this.request('/settings/contact');
        return response.data ?? { phone: '', email: '', instagram: '', whatsapp: '', website: '' };
    }
}
// 📊 Singleton instance for storefront API
// Note: Base URL will be overridden by the consuming app if needed
const STOREFRONT_BASE_URL = typeof process !== 'undefined' && process.env?.VITE_STOREFRONT_API_BASE_URL
    ? process.env.VITE_STOREFRONT_API_BASE_URL
    : 'http://localhost:3001/api';
export const storefrontApi = new StorefrontApiClient(STOREFRONT_BASE_URL);
